import { useState, useEffect } from 'react'
import { useTemplate } from '../../api/templates'
import { useGeometryStore } from '../../store/geometryStore'
import type { TemplateJson, TemplateField } from '../../types/template'
import { FieldType } from '../../types/template'
import { resolveAutoFill } from '../../utils/geo/autoFill'
import { isValidMgrs } from '../../utils/geo/mgrs'
import MgrsInput from '../geo/MgrsInput'
import CoordinateInput from '../geo/CoordinateInput'
import GeometryField from '../geo/GeometryField'
import type { GeoJsonGeometry } from '../../types/geo'

interface Props {
  templateName: string
  onSubmit: (filled: TemplateJson & { _values: Record<string, unknown> }) => void
  onBack: () => void
}

export default function CollectionForm({ templateName, onSubmit, onBack }: Props) {
  const { data: template, isLoading } = useTemplate(templateName)
  const { drawnGeometry } = useGeometryStore()
  const [values, setValues] = useState<Record<string, unknown>>({})

  // Auto-fill geo fields from drawn geometry
  useEffect(() => {
    if (!template || !drawnGeometry) return
    const autoFilled: Record<string, unknown> = {}
    for (const field of template.fields) {
      const autoVal = resolveAutoFill(field.type, drawnGeometry)
      if (autoVal !== undefined) autoFilled[field.id] = autoVal
    }
    setValues((prev) => ({ ...autoFilled, ...prev }))
  }, [template, drawnGeometry])

  function setValue(id: string, val: unknown) {
    setValues((prev) => ({ ...prev, [id]: val }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!template) return
    onSubmit({ ...template, _values: values })
  }

  if (isLoading) {
    return <div className="animate-pulse text-sm text-muted-foreground">Loading template...</div>
  }

  if (!template) {
    return <div className="text-sm text-destructive">Template not found.</div>
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold">{template.meta.name}</h3>
        {template.meta.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{template.meta.description}</p>
        )}
        {template.meta.classification && (
          <span className="inline-block text-xs font-mono text-yellow-400 mt-1">
            {template.meta.classification}
          </span>
        )}
      </div>

      {template.fields.map((field) => (
        <FieldRenderer
          key={field.id}
          field={field}
          value={values[field.id]}
          onChange={(v) => setValue(field.id, v)}
          drawnGeometry={drawnGeometry}
        />
      ))}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 rounded-md border border-border px-4 py-2 text-sm hover:bg-accent/50"
        >
          ← Back
        </button>
        <button
          type="submit"
          className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Review →
        </button>
      </div>
    </form>
  )
}

type DrawnGeometryType = ReturnType<typeof useGeometryStore.getState>['drawnGeometry']

interface FieldRendererProps {
  field: TemplateField
  value: unknown
  onChange: (v: unknown) => void
  drawnGeometry: DrawnGeometryType
}

function FieldRenderer({ field, value, onChange, drawnGeometry: _drawnGeometry }: FieldRendererProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-sm font-medium">{field.label}</label>
        {field.required && <span className="text-xs text-destructive">*</span>}
      </div>
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}

      {renderInput(field, value, onChange)}
    </div>
  )
}

function renderInput(
  field: TemplateField,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  const strVal = typeof value === 'string' ? value : ''

  switch (field.type) {
    case FieldType.CoordinateMgrs:
      return (
        <MgrsInput
          value={strVal}
          onChange={onChange}
          placeholder={field.placeholder}
        />
      )

    case FieldType.CoordinateAny:
      return (
        <CoordinateInput
          value={strVal}
          onChange={onChange}
          placeholder={field.placeholder}
        />
      )

    case FieldType.CoordinateDD: {
      const ddVal = (value as { lat?: number; lon?: number }) ?? {}
      return (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Latitude</label>
            <input
              type="number"
              step="any"
              value={ddVal.lat ?? ''}
              onChange={(e) => onChange({ ...ddVal, lat: parseFloat(e.target.value) })}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="-90 to 90"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Longitude</label>
            <input
              type="number"
              step="any"
              value={ddVal.lon ?? ''}
              onChange={(e) => onChange({ ...ddVal, lon: parseFloat(e.target.value) })}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="-180 to 180"
            />
          </div>
        </div>
      )
    }

    case FieldType.Bbox: {
      const bboxVal = (value as { minLon?: number; minLat?: number; maxLon?: number; maxLat?: number }) ?? {}
      return (
        <div className="grid grid-cols-2 gap-2">
          {(['minLon', 'minLat', 'maxLon', 'maxLat'] as const).map((key) => (
            <div key={key} className="space-y-1">
              <label className="text-xs text-muted-foreground">{key}</label>
              <input
                type="number"
                step="any"
                value={bboxVal[key] ?? ''}
                onChange={(e) => onChange({ ...bboxVal, [key]: parseFloat(e.target.value) })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          ))}
        </div>
      )
    }

    case FieldType.GeoJsonPolygon:
    case FieldType.GeoJsonPoint:
    case FieldType.GeoJsonLinestring:
    case FieldType.GeoJsonGeometry:
      return (
        <GeometryField
          value={(value as GeoJsonGeometry) ?? null}
          onChange={onChange}
          onRedraw={() => useGeometryStore.getState().closeModal()}
        />
      )

    case FieldType.MgrsList: {
      const list = (value as string[]) ?? []
      return <MgrsListInput value={list} onChange={onChange} />
    }

    case FieldType.Select:
      return (
        <select
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <option value="">Select...</option>
          {field.options?.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )

    case FieldType.Textarea:
      return (
        <textarea
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          placeholder={field.placeholder}
          rows={4}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-ring"
        />
      )

    case FieldType.Number:
      return (
        <input
          type="number"
          step="any"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          required={field.required}
          placeholder={field.placeholder}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
      )

    case FieldType.Date:
      return (
        <input
          type="date"
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
      )

    case FieldType.Datetime:
      return (
        <input
          type="datetime-local"
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
      )

    case FieldType.Boolean:
      return (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded border-input"
          />
          <span className="text-sm">{field.label}</span>
        </label>
      )

    default:
      // Unknown type — degrade to text input
      return (
        <div className="space-y-1">
          <input
            type="text"
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <span className="text-xs text-muted-foreground/60">(unknown type: {field.type})</span>
        </div>
      )
  }
}

function MgrsListInput({
  value,
  onChange,
}: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [input, setInput] = useState('')
  const [error, setError] = useState('')

  function addTag() {
    const trimmed = input.trim().toUpperCase()
    if (!trimmed) return
    if (!isValidMgrs(trimmed)) {
      setError(`Invalid MGRS: ${trimmed}`)
      return
    }
    onChange([...value, trimmed])
    setInput('')
    setError('')
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value.toUpperCase()); setError('') }}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          placeholder="18S UJ 23395 09512"
          className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <button
          type="button"
          onClick={addTag}
          className="rounded-md bg-muted px-3 py-1 text-sm hover:bg-accent"
        >
          Add
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((mgrs, i) => (
            <span key={i} className="flex items-center gap-1 rounded bg-blue-500/20 text-blue-400 px-2 py-0.5 text-xs font-mono">
              {mgrs}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="hover:text-red-400"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
